/**
 * Single authoritative place for resolving what text and color to display
 * for a skill rating label on the frontend.
 *
 * All components that render a rating badge should call resolveRatingLabel()
 * instead of implementing their own fallback logic.
 *
 * Resolution rules:
 *   color — uses statusColor (pre-resolved by server) when present;
 *            falls back to client-side lookup by label_slug via ratingScaleLabels
 *            when the server value is absent (e.g. checkin notes cards).
 *
 *   text  — statusLabel (pre-resolved display name) → 'Not Checked-In' when
 *            status === 'not_checked_in' → labelName from ratingScaleLabels lookup
 *            → raw status string as last resort.
 */

export type RatingScaleLabel = {
  labelName: string
  labelSlug: string
  color: string
  scaleValue: number
  description?: string
}

export type RatingLabelInput = {
  /** Label slug or legacy label name string from the server (e.g. "exceeding", "not_checked_in"). */
  status?: string | null
  /** Pre-resolved hex color from the server. Used as-is when present. */
  statusColor?: string | null
  /** Pre-resolved human-readable label name from the server. */
  statusLabel?: string | null
  /**
   * Rating scale labels from the checkin store — used as a client-side
   * fallback when statusColor is absent (e.g. checkin-skill-card-notes
   * rendering per-role ratings without a pre-resolved server color).
   */
  ratingScaleLabels?: RatingScaleLabel[]
}

export type RatingLabelResult = {
  /** Display text for the rating badge. undefined means nothing to show. */
  text: string | undefined
  /** Hex color for --rating-color. null means no color (default styling applies). */
  color: string | null
}

export function resolveRatingLabel({
  status,
  statusColor,
  statusLabel,
  ratingScaleLabels,
}: RatingLabelInput): RatingLabelResult {
  // --- Color + label lookup ---
  // Prefer the server-resolved value; fall back to client-side slug lookup.
  let color: string | null = statusColor ?? null
  let foundLabel: RatingScaleLabel | undefined
  if (status && ratingScaleLabels?.length) {
    foundLabel =
      ratingScaleLabels.find((l) => l.labelSlug === status) ??
      ratingScaleLabels.find(
        (l) => l.labelName?.toLowerCase() === status.toLowerCase()
      )
    if (!color) color = foundLabel?.color ?? null
  }

  // --- Text ---
  let text: string | undefined
  if (statusLabel) {
    text = statusLabel
  } else if (status === 'not_checked_in') {
    text = 'Not Checked-In'
  } else if (foundLabel?.labelName) {
    text = foundLabel.labelName
  } else {
    text = status ?? undefined
  }

  return { text, color }
}
